import React, { useState, useEffect } from 'react';
import useAuthStore from '../store/useAuthStore';
import { DesktopService } from '../services/desktopService';
import { useDesktopStatus } from '../hooks/useDesktopStatus';
import { motion, AnimatePresence } from 'framer-motion';
import ViewToggle from '../components/ViewToggle';

const LaptopIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
    </svg>
);

const BatteryIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 10.5h.375c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125H21M4.5 10.5h6.75V15H4.5v-4.5zM3.75 18h15A2.25 2.25 0 0021 15.75v-6a2.25 2.25 0 00-2.25-2.25h-15A2.25 2.25 0 001.5 9.75v6A2.25 2.25 0 003.75 18z" />
    </svg>
);

const CpuIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" />
    </svg>
);

const PowerIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
    </svg>
);

const FolderIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" /></svg>;

const StatMini = ({ icon, label, value, progress, colorClass }) => (
    <div className="flex-1 min-w-[120px]">
        <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
                <span className={colorClass}>{icon}</span>
                <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider">{label}</span>
            </div>
            <span className="text-xs font-bold text-[var(--text-main)]">{value}</span>
        </div>
        <div className="h-1 w-full bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
            <div
                className={`h-full rounded-full transition-all duration-1000 ${colorClass.replace('text-', 'bg-')}`}
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
        </div>
    </div>
);

const DeviceCard = ({ deviceId, initialData }) => {
    const { user } = useAuthStore();
    const { isOnline, deviceData, stats } = useDesktopStatus(deviceId);
    const [isShuttingDown, setIsShuttingDown] = useState(false);

    const handleShutdown = async () => {
        if (!window.confirm("Are you sure you want to quit the desktop application on this device? This will stop all local AI features until the app is restarted manually.")) return;

        setIsShuttingDown(true);
        try {
            await DesktopService.shutdownAgent(user.uid, deviceId);
        } catch (err) {
            console.error("Shutdown failed:", err);
            setIsShuttingDown(false);
        }
    };

    const currentData = deviceData || initialData;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="group relative cursor-default h-full"
        >
            {/* Hover Highlight Overlay (Similar to Spaces/Agents) */}
            <div className="absolute -inset-px bg-gradient-to-b from-[var(--border-medium)] to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="relative h-full p-6 bg-[var(--bg-secondary)] dark:bg-[var(--bg-tertiary)]/20 backdrop-blur-sm border border-[var(--border-subtle)] rounded-xl shadow-sm group-hover:border-[var(--border-medium)] transition-all duration-300 overflow-hidden">
                {/* Status Glow */}
                <div className={`absolute -top-10 -right-10 w-32 h-32 blur-3xl opacity-10 transition-colors duration-1000 pointer-events-none ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />

                <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl border flex items-center justify-center transition-all duration-500 ${isOnline ? 'bg-green-500/10 border-green-500/20 text-green-500' : 'bg-red-500/10 border-red-500/20 text-red-500 font-bold'}`}>
                            <LaptopIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-[15px] font-semibold text-[var(--text-main)] tracking-tight leading-tight truncate max-w-[140px]">
                                {currentData?.computer_name || currentData?.computerName || currentData?.hostname || 'Unknown Device'}
                            </h3>
                            <div className="flex items-center gap-1.5 mt-1">
                                <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-[var(--text-dim)]'}`} />
                                <span className={`text-[10px] font-bold uppercase tracking-widest ${isOnline ? 'text-green-500/80' : 'text-[var(--text-dim)]'}`}>
                                    {isOnline ? 'Online' : 'Offline'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleShutdown}
                        disabled={!isOnline || isShuttingDown}
                        className="p-1.5 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Quit Desktop App"
                    >
                        {isShuttingDown ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        ) : (
                            <PowerIcon className="w-4 h-4" />
                        )}
                    </button>
                </div>

                {isOnline && stats ? (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <StatMini
                            icon={<CpuIcon className="w-3.5 h-3.5" />}
                            label="CPU"
                            value={`${stats.cpu_load}%`}
                            progress={stats.cpu_load}
                            colorClass="text-blue-400"
                        />
                        <StatMini
                            icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" /></svg>}
                            label="RAM"
                            value={`${stats.ram_load}%`}
                            progress={stats.ram_load}
                            colorClass="text-orange-400"
                        />
                        <StatMini
                            icon={<BatteryIcon className="w-3.5 h-3.5" />}
                            label="Battery"
                            value={`${stats.battery_level}%`}
                            progress={stats.battery_level}
                            colorClass={stats.battery_level > 20 ? 'text-green-400' : 'text-red-400'}
                        />
                        <StatMini
                            icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75m-16.5-3.75v3.75" /></svg>}
                            label="Disk"
                            value={`${stats.disk_percent}%`}
                            progress={stats.disk_percent}
                            colorClass="text-purple-400"
                        />
                    </div>
                ) : (
                    <div className="py-2 text-center">
                        <p className="text-[11px] text-[var(--text-dim)] italic font-medium">
                            {isOnline ? 'Waiting for telemetry...' : `Last seen ${currentData?.last_seen?.toDate().toLocaleString() || 'never'}`}
                        </p>
                    </div>
                )}

                <div className="mt-6 pt-4 border-t border-[var(--border-subtle)] flex items-center justify-between transition-colors">
                    <span className="text-[9px] font-bold text-[var(--text-dim)] uppercase tracking-wider leading-none">
                        IP: {currentData?.ip_address || '---'}
                    </span>
                    <span className="text-[9px] font-bold text-[var(--text-dim)] uppercase tracking-wider leading-none bg-[var(--bg-tertiary)]/50 px-1.5 py-0.5 rounded border border-[var(--border-subtle)]">
                        v{currentData?.version || '0.0.0'}
                    </span>
                </div>

                {/* Hover Highlight Overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[var(--accent-primary)]/[0.01] to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </motion.div>
    );
};

const DeviceListRow = ({ deviceId, initialData }) => {
    const { user } = useAuthStore();
    const { isOnline, deviceData, stats } = useDesktopStatus(deviceId);
    const [isShuttingDown, setIsShuttingDown] = useState(false);

    const handleShutdown = async (e) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to quit the desktop application on this device?")) return;
        setIsShuttingDown(true);
        try {
            await DesktopService.shutdownAgent(user.uid, deviceId);
        } catch (err) {
            console.error("Shutdown failed:", err);
            setIsShuttingDown(false);
        }
    };

    const currentData = deviceData || initialData;

    return (
        <div className="group relative flex items-center justify-between p-3.5 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-xl hover:border-[var(--border-medium)] transition-all duration-200 shadow-sm pr-12 cursor-default">
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${isOnline ? 'bg-green-500/10 border-green-500/20 text-green-500' : 'bg-red-500/10 border-red-500/20 text-red-500'}`}>
                    <LaptopIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-[14px] font-medium text-[var(--text-main)] truncate leading-tight">
                        {currentData?.computer_name || currentData?.computerName || currentData?.hostname || 'Unknown Device'}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        <div className={`w-1 h-1 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-[var(--text-dim)]'}`} />
                        <span className="text-[10px] font-bold text-[var(--text-dim)] uppercase tracking-widest">
                            {isOnline ? 'Online' : 'Offline'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-8 shrink-0">
                {isOnline && stats ? (
                    <div className="flex items-center gap-6">
                        <div className="flex flex-col items-end">
                            <span className="text-[9px] font-bold text-[var(--text-dim)] uppercase tracking-wider">CPU</span>
                            <span className="text-[11px] font-semibold text-[var(--text-main)]">{stats.cpu_load}%</span>
                        </div>
                        <div className="flex flex-col items-end">
                            <span className="text-[9px] font-bold text-[var(--text-dim)] uppercase tracking-wider">RAM</span>
                            <span className="text-[11px] font-semibold text-[var(--text-main)]">{stats.ram_load}%</span>
                        </div>
                    </div>
                ) : (
                    <span className="text-[10px] italic text-[var(--text-dim)]">
                        {isOnline ? 'Wait...' : 'Offline'}
                    </span>
                )}
                <span className="text-[10px] font-bold text-[var(--text-dim)] uppercase tracking-widest bg-[var(--bg-tertiary)]/50 px-2 py-0.5 rounded border border-[var(--border-subtle)] leading-none text-center min-w-[60px]">
                    Device
                </span>
            </div>

            <div className="absolute top-1/2 -translate-y-1/2 right-3 flex items-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                <button
                    onClick={handleShutdown}
                    disabled={!isOnline || isShuttingDown}
                    className="p-1.5 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 rounded-md transition-all disabled:opacity-30 shadow-sm"
                    title="Quit Desktop App"
                >
                    {isShuttingDown ? (
                        <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    ) : (
                        <PowerIcon className="w-3.5 h-3.5" />
                    )}
                </button>
            </div>
        </div>
    );
};

const DevicesPage = () => {
    const { user } = useAuthStore();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('devices_view_mode') || 'grid');

    useEffect(() => {
        localStorage.setItem('devices_view_mode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        if (!user) return;

        const loadDevices = async () => {
            const fetched = await DesktopService.getUserDevices(user.uid);
            setDevices(fetched);
            setLoading(false);
        };

        loadDevices();
    }, [user]);

    return (
        <div className="pb-20 min-h-full bg-[var(--bg-main)] flex flex-col font-sans selection:bg-[var(--accent-primary)]/20">
            <header className="px-4 md:px-8 py-8 md:py-14 max-w-7xl mx-auto w-full flex flex-col md:flex-row md:justify-between md:items-end gap-6">
                <div className="space-y-1">
                    <h1 className="text-3xl md:text-4xl font-light text-[var(--text-main)] tracking-tighter">Devices</h1>
                    <p className="text-sm text-[var(--text-muted)] font-medium">Monitor and manage your connected desktop agent instances.</p>
                </div>

                <div className="flex items-center gap-3">
                    <ViewToggle mode={viewMode} onChange={setViewMode} />
                </div>
            </header>

            <div className="px-4 md:px-8 max-w-7xl mx-auto w-full flex-1">
                <AnimatePresence mode="wait">
                    {loading ? (
                        <div key="loading" className="flex items-center justify-center py-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent-primary)]"></div>
                        </div>
                    ) : devices.length === 0 ? (
                        <motion.div
                            key="empty-state"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="max-w-2xl mx-auto"
                        >
                            <div className="flex flex-col items-center justify-center py-20 text-center mb-12">
                                <div className="w-24 h-24 bg-[var(--bg-tertiary)]/30 backdrop-blur-md rounded-3xl flex items-center justify-center mb-6 border border-[var(--border-subtle)] shadow-2xl relative group">
                                    <div className="absolute inset-0 bg-gradient-to-tr from-[var(--accent-primary)]/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <LaptopIcon className="w-10 h-10 text-[var(--text-dim)]" />
                                </div>
                                <h2 className="text-2xl font-semibold text-[var(--text-main)] mb-3 tracking-tight">Connect your first device</h2>
                                <p className="text-sm text-[var(--text-muted)] max-w-md leading-relaxed">
                                    Register your computer to unlock powerful local AI capabilities like remote terminal access and screen analysis.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <h3 className="text-xs font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">Setup Instructions</h3>
                                    <div className="space-y-4">
                                        {[
                                            { step: 1, title: "Download App", desc: "Get the Intrellian Desktop application for your computer." },
                                            { step: 2, title: "Authenticate", desc: "Sign in with your Intrellian account within the desktop app." },
                                            { step: 3, title: "Register", desc: "The app will automatically register this device to your dashboard." }
                                        ].map((item) => (
                                            <div key={item.step} className="flex gap-4 p-4 rounded-2xl bg-[var(--bg-tertiary)]/10 border border-[var(--border-subtle)]/50">
                                                <div className="w-6 h-6 rounded-full bg-[var(--text-main)] text-[var(--bg-main)] flex items-center justify-center text-[10px] font-bold shrink-0">
                                                    {item.step}
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-semibold text-[var(--text-main)]">{item.title}</h4>
                                                    <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed">{item.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <h3 className="text-xs font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">Why connect?</h3>
                                    <div className="space-y-3">
                                        {[
                                            { icon: <CpuIcon className="w-4 h-4" />, title: "Remote Vision", desc: "Let Omni see your screen to help with complex tasks." },
                                            { icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" /></svg>, title: "Terminal Shell", desc: "Execute commands and scripts remotely from your mobile chat." },
                                            { icon: <FolderIcon className="w-4 h-4" />, title: "File Control", desc: "Read and write local files as part of your AI workflows." }
                                        ].map((benefit, i) => (
                                            <div key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--bg-tertiary)]/20 transition-colors">
                                                <div className="w-8 h-8 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center text-[var(--text-muted)] shrink-0 border border-[var(--border-subtle)]">
                                                    {benefit.icon}
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-semibold text-[var(--text-main)]">{benefit.title}</h4>
                                                    <p className="text-[11px] text-[var(--text-dim)]">{benefit.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            layout
                            className={viewMode === 'grid'
                                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20 auto-rows-fr"
                                : "flex flex-col gap-3 mb-20"}
                        >
                            {devices.map(device => (
                                <motion.div
                                    key={device.id}
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={viewMode === 'grid' ? "h-full" : ""}
                                >
                                    {viewMode === 'grid' ? (
                                        <DeviceCard deviceId={device.id} initialData={device} />
                                    ) : (
                                        <DeviceListRow deviceId={device.id} initialData={device} />
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default DevicesPage;
